import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { OrderDetailModel } from 'src/app/models/preliminaries/order-detail.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { CostCenterPriceService } from 'src/app/services/archive/cost-center-price.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { OrderDetailService } from 'src/app/services/preliminaries/order-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-order-price',
  templateUrl: './order-price.component.html',
  styleUrls: ['./order-price.component.scss'],
  standalone: false
})
export class OrderPriceComponent implements OnInit {

  articleId: number = 0;
  swInvoiceUnitM2: boolean = false;
  costCenterId: number = 0;
  disable: boolean = false;
  discountValue: number = 0;
  formPriceOrder: FormGroup = new FormGroup({});
  listArticle: any[] = [];
  listBranch: any[] = [];
  listRentalUnit: any[] = [];
  listUnit: any[] = [{ label: 'UNIDAD', value: false }, { label: 'M2', value: true }];
  orderDetailContract: OrderDetailModel = new OrderDetailModel();
  suscription: Subscription;

  constructor(private costCenterPriceService: CostCenterPriceService,
    private formBuilder: FormBuilder,
    private orderDetailService: OrderDetailService,
    private toastHandlerService: ToastHandlerService,
    private branchService: BranchService,
    private articleService: ArticleService,
    public config: DynamicDialogConfig,
    @Optional() private ref: DynamicDialogRef) {
    this.getAllLists();
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.orderDetailContract = inputs.orderDetail;
      this.costCenterId = inputs.costCenterId;
      this.disable = inputs.disable;
    });
    this.validateForm(this.orderDetailContract);
    this.handleInitialConfig();
  }

  async changeInvoiceUnit(changeUnit: boolean) {
    this.swInvoiceUnitM2 = this.formPriceOrder.get("swInvoiceM2")?.value;
    this.articleId = this.formPriceOrder.get("articleId")?.value;
    if (this.costCenterId && this.costCenterId != 0 && this.articleId && this.articleId != 0) {
      //Busca el valor del m2 del cco seleccionado
      this.getBaseUnitValueByKeys(this.costCenterId, this.articleId, this.swInvoiceUnitM2, changeUnit);
    }
  }

  changeRateDiscount() {
    //obtengo el valor base y el % descuento
    const baseUnitValue = this.formPriceOrder.get("baseUnitValue")?.value;
    const rateDiscount = this.formPriceOrder.get("rateDiscount")?.value;
    //Calculo el valor del descuento
    this.discountValue = ((baseUnitValue * rateDiscount) / 100);
    //Al valor base le resto el valor descuento para definir el valor unitario
    const unitValue = baseUnitValue - this.discountValue;
    this.formPriceOrder.controls["unitValue"].patchValue(unitValue);
  }

  changeUnitValue() {
    //obtengo el valor base y el valor unitario
    const baseUnitValue = this.formPriceOrder.get("baseUnitValue")?.value;
    const unitValue = this.formPriceOrder.get("unitValue")?.value;
    let rateDiscount;
    if (baseUnitValue == 0) {
      rateDiscount = 100;
      this.discountValue = 0;
    } else {
      //Calculo el nuevo % descuento
      rateDiscount = ((baseUnitValue - unitValue) / baseUnitValue * 100);
      this.discountValue = ((baseUnitValue * rateDiscount) / 100);
    }
    this.formPriceOrder.controls["rateDiscount"].patchValue(rateDiscount);
  }

  close(change: boolean) {
    const data: ModalObjectType = {};
    if (change) {
      data.params = { reload: true, detail: this.orderDetailContract };
    }
    this.ref.close(data);
  }

  async getAllLists() {
    try {
      const [branches, articles, rentalUnits] = await Promise.all([
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
        firstValueFrom(this.articleService.getAllArticlesForSelectAsync()),
        firstValueFrom(this.orderDetailService.getRentalUnitForSelect()),
      ]);
      this.listBranch = branches;
      this.listArticle = articles;
      this.listRentalUnit = rentalUnits;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  async getBaseUnitValueByKeys(costCenterId: number, articleId: number, swInvoiceUnitM2: boolean, changeUnit: boolean) {
    try {
      const response = await firstValueFrom(this.costCenterPriceService.getBaseUnitValueByKeys(costCenterId, articleId, swInvoiceUnitM2));
      const value = response === -1 ? 0 : response;
      this.formPriceOrder.controls["baseUnitValue"].patchValue(value);
      if (this.orderDetailContract.baseUnitValue == 0) {
        this.formPriceOrder.controls["unitValue"].patchValue(value);
      }
      if (changeUnit) {
        this.changeRateDiscount();
      }
    } catch (error) {
      console.error("Error al obtener el valor base de la unidad:", error);
    }
  }

  async handleInitialConfig() {
    if (this.orderDetailContract.baseUnitValue != 0) {
      const orderDetail = this.orderDetailContract;
      this.discountValue = ((orderDetail.baseUnitValue * orderDetail.rateDiscount) / 100);
    } else {
      this.changeInvoiceUnit(false);
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formPriceOrder.valid) {
      return this.showValidationMessages();
    }
    const orderDetailContract = this.formPriceOrder.getRawValue() as OrderDetailModel;
    this.orderDetailContract.costCenterPrice = null;
    this.orderDetailContract.unitValue = orderDetailContract.unitValue;
    this.orderDetailContract.baseUnitValue = orderDetailContract.baseUnitValue;
    this.orderDetailContract.rateDiscount = orderDetailContract.rateDiscount;
    this.orderDetailContract.swInvoiceM2 = orderDetailContract.swInvoiceM2;
    try {
      this.orderDetailContract = await firstValueFrom(this.orderDetailService.updateOrderPrice(this.orderDetailContract));
      this.close(true);
      this.formPriceOrder.controls["baseUnitValue"].disable();
      this.toastHandlerService.showToast('success', 'El pedido fue actualizado exitosamente.', '');
    } catch (error) {
      console.error('Error al actualizar el pedido.', error);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      orderId: {
        required: 'El campo pedido es obligatorio.',
        min: 'Debes seleccionar un pedido válido.',
      },
      articleId: {
        required: 'El campo artículo es obligatorio.',
        min: 'Debes seleccionar un artículo válido.',
      },
      baseUnitValue: {
        required: 'El campo precio lista es obligatorio.',
        min: 'El Precio Lista mínima permitida es 1.',
      },
      unitValue: {
        required: 'El campo Valor Unitario es obligatorio.',
        min: 'Debe ingresar un valor igual o superior a Cero (0).',
        max: 'No puede ingresar un valor mayor a 99,999,999,999.99 en este campo.'
      },
      rateDiscount: {
        required: 'El campo Descuento es obligatorio.',
        min: 'No puede ingresar un valor menor a -999.99999 en el campo Descuento.',
        max: 'No puede ingresar un valor mayor a 100 en el campo Descuento.'
      },
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formPriceOrder.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateFormControls() {
    Object.keys(this.formPriceOrder.controls).forEach((key) => {
      const control = this.formPriceOrder.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }

  validateForm(orderDetail: OrderDetailModel) {
    this.formPriceOrder = this.formBuilder.group({
      orderId: [orderDetail.orderId, [Validators.required, Validators.min(1)]],
      branchId: [orderDetail.order?.branchId, [Validators.required]],
      consecutive: [orderDetail?.order?.consecutive],
      articleId: [orderDetail.articleId, [Validators.required, Validators.min(1)]],
      rentalUnitId: [orderDetail.rentalUnitId, [Validators.required, Validators.min(1)]],
      swInvoiceM2: [orderDetail.swInvoiceM2, [Validators.required]],
      baseUnitValue: [orderDetail.baseUnitValue, [Validators.required, Validators.min(1)]],
      unitValue: [orderDetail.unitValue, [Validators.required, Validators.min(0), Validators.max(99999999999.99)]],
      rateDiscount: [orderDetail.rateDiscount, [Validators.required, Validators.min(-999.99999), Validators.max(100)]],
    });

    this.formPriceOrder.disable();
    if (!this.disable) {
      this.formPriceOrder.controls["rateDiscount"].enable();
      this.formPriceOrder.controls["unitValue"].enable();
      if (this.orderDetailContract.order!.costCenter.swInvoiceM2) {
        this.formPriceOrder.controls["swInvoiceM2"].enable();
      }
    }
  }
}
